import { useContext, useRef } from "react";
import { UseMutateFunction } from "@tanstack/react-query";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import ImportFileUploader from "src/Pages/Company/OrganizationStructure/sharedComponents/ImportFileUploader/ImportFileUploader";
import __ from "src/utils/Translations";

interface UseImportModalOptions {
  title: string;
  mutates: UseMutateFunction<unknown, unknown, File, unknown>[];
}

const useImportModal = ({ title, mutates }: UseImportModalOptions) => {
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const selectedFileRef = useRef<File | null>(null);
  const isImportingRef = useRef(false);

  const open = () => {
    selectedFileRef.current = null;
    isImportingRef.current = false;

    const handleCleanup = () => {
      selectedFileRef.current = null;
      isImportingRef.current = false;
      resetModal();
    };

    const updateModal = (uploadedFile: File | null, isImporting = false) => {
      setModal({
        maxWidth: 628,
        open: true,
        title: <>{title}</>,
        customElements: (
          <ImportFileUploader
            uploadedFile={uploadedFile}
            onFileAccepted={(file: File) => {
              selectedFileRef.current = file;
              updateModal(file);
            }}
            onFileRemove={() => updateModal(null)}
          />
        ),
        actions: [
          {
            label: __("Anuluj"),
            variant: "outlined",
            disabled: isImporting,
            callbackFn: handleCleanup,
          },
          {
            label: __("Importuj"),
            variant: "contained",
            disabled: !uploadedFile || isImporting,
            callbackFn: () => {
              const file = selectedFileRef.current;

              if (!file || isImportingRef.current) {
                return;
              }

              isImportingRef.current = true;
              updateModal(file, true);

              const callbacks = {
                onSuccess: handleCleanup,
                onError: () => {
                  isImportingRef.current = false;
                  updateModal(file, false);
                },
              };

              mutates.forEach((mutate) => mutate(file, callbacks));
            },
          },
        ],
      });
    };

    updateModal(null);
  };

  return { open };
};

export default useImportModal;
